import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  MenuItem,
  IconButton,
  Typography,
  Card,
  CardContent,
  Grid,
  makeStyles,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import DeleteIcon from "@material-ui/icons/Delete";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import axios from "src/utils/axios";
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme) => ({
  sectionLabel: {
    fontSize: 15,
    fontWeight: 700,
    color: "#1b84ff",
    marginBottom: theme.spacing(2),
    display: "inline-block",
    position: "relative",
    "&::after": {
      content: '""',
      display: "block",
      width: 40,
      height: 3,
      background: "linear-gradient(90deg, #00B2FF, #0079FF)",
      borderRadius: 2,
      marginTop: 4,
    },
  },
  field: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 14,
      backgroundColor: "#fff",
      transition: "all 0.2s ease-in-out",
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#4DA3FF",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderWidth: 2,
        borderColor: "#1B84FF",
        boxShadow: "0 0 6px rgba(27,132,255,0.3)",
      },
    },
  },
  phoneInput: {
    "& .form-control": {
      height: 44,
      borderRadius: 12,
      width: "100%",
      fontSize: 14,
      border: "1px solid #ced6e0",
      transition: "all 0.2s ease-in-out",
    },
    "& .form-control:hover, & .form-control:focus": {
      borderColor: "#1B84FF",
      boxShadow: "0 0 6px rgba(27,132,255,0.3)",
      outline: "none",
    },
    "& .flag-dropdown": {
      borderRadius: "12px 0 0 12px",
    },
  },
}));

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

export default function PhoneBlock({
  value = [],
  onChange,
  sessionUser,
  error,
  onSelectExistingConsumer,
}) {
  const classes = useStyles();
  const { t } = useTranslation('overview');
  const [phoneTypes, setPhoneTypes] = useState([]);
  const [paramPhonesType, setParamPhonesType] = useState("");
  const [newTel, setNewTel] = useState("");
  const [phones, setPhones] = useState(value || []);
  const [tempPhone, setTempPhone] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, type: "info", message: "" });
  

  // 🆕 Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [existingConsumer, setExistingConsumer] = useState(null);

  useEffect(() => {
    if (Array.isArray(value)) {
      setPhones(value);

      onChange && onChange(value);
    }
  }, [value]);

  useEffect(() => {
    const GetPhoneTypes = async () => {
      const phoneData = sessionStorage.getItem("phoneTypeData");
      if (
        phoneData &&
        JSON.parse(phoneData)[1]?.LanguageCode === localStorage.getItem("i18nextLng")
      ) {
        const parsed = JSON.parse(phoneData);
        setPhoneTypes(parsed);
        setParamPhonesType(parsed[1].Id);
      } else {
        const response = await axios.get(`/GetPhoneTypes`);
        if (response.data.length > 0) {
          setPhoneTypes(response.data);
          setParamPhonesType(response.data[1].Id);
          sessionStorage.setItem("phoneTypeData", JSON.stringify(response.data));
        }
      }
    };
    GetPhoneTypes();
  }, []);

  const handleOnAdd = async () => {
  if (!newTel || !paramPhonesType) return;
  const country = tempPhone?.country || "";
  const number = tempPhone?.number || newTel;

  try {
    const { data: response } = await axios.get(`/CheckPhoneNumber/${newTel}`);
    let ResponseData = response;

    if (sessionUser?.CustomerTypeId === 9 || sessionUser?.CustomerTypeId === 10) {
      ResponseData = "";
    }

    if (ResponseData && ResponseData.length > 0) {
      setExistingConsumer(ResponseData[0]);
      setDialogOpen(true);
      return;
    }

    const newPhone = {
      PhoneType: paramPhonesType,
      CountryCode: country,
      Phone: number,
    };

    setPhones((prev) => {
      const next = [...prev, newPhone];
      Promise.resolve().then(() => {
        onChange && onChange(next);
      });
      return next;
    })

    setNewTel("");
    setTempPhone(null);
  } catch (err) {
    console.error("CheckPhoneNumber error:", err);
  }
};

  const handleDialogYes = () => {
  if (existingConsumer && typeof onSelectExistingConsumer === "function") {
    Promise.resolve().then(() => {
      onSelectExistingConsumer(existingConsumer.ConsumerId);
    });
  }
  setDialogOpen(false);
};

  const handleDialogNo = () => {
    setDialogOpen(false);
    setExistingConsumer(null);
  };

  const deletePhoneItem = (index) => {
    setPhones((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Card style={{ borderRadius: 16, marginTop: 16 }}>
      <CardContent>
        <Typography className={classes.sectionLabel}>
          📞{t("phoneNumbers")}
        </Typography>

        {/* ➕ Add Row */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              select
              variant="outlined"
              size="small"
              label={t("type", "Type")}
              value={paramPhonesType}
              onChange={(e) => setParamPhonesType(e.target.value)}
              className={classes.field}
              InputProps={{ style: { height: 44 } }}
            >
              {phoneTypes.map((p) => (
                <MenuItem key={p.Id} value={p.Id}>
                  {p.Dsc}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={7}>
            <PhoneInput
              country={sessionUser?.TenantCode?.toLowerCase() || "pl"}
              enableSearch
              value={newTel}
              onChange={(val, data) => {
                setNewTel(val);
                const country = data?.dialCode || "";
                const number = val.startsWith(country)
                  ? val.slice(country.length)
                  : val;
                setTempPhone({ country, number });
              }}
              containerClass={classes.phoneInput}
              inputStyle={{
                borderColor: error ? "#f44336" : "#ced6e0",
              }}
            />
          </Grid>

          <Grid item xs={12} md={1}>
            <button
              onClick={handleOnAdd}
              style={{
                height: 32,
                width: 32,
                borderRadius: "50%",
                border: "none",
                background: "linear-gradient(90deg,#00B2FF,#0079FF)",
                color: "#fff",
                fontWeight: 700,
                fontSize: 18,
                cursor: "pointer",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                transition: "all 0.2s ease",
              }}
            >
              +
            </button>
          </Grid>
        </Grid>

        {/* 📜 Added Phones */}
        <Box mt={2} style={{ height: 190, overflowY: "auto" }}>
          <Grid container direction="column" spacing={2}>
            {phones.map((item, index) => (
              <Grid item key={index}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      select
                      variant="outlined"
                      size="small"
                      value={item.PhoneType}
                      className={classes.field}
                      InputProps={{
                        readOnly: true,
                        style: {
                          height: 44,
                          borderRadius: 12,
                          backgroundColor: "#f9f9f9",
                        },
                      }}
                    >
                      {phoneTypes.map((p) => (
                        <MenuItem key={p.Id} value={p.Id}>
                          {p.Dsc}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={7}>
                    <PhoneInput
                      value={`+${item.CountryCode}${item.Phone}`}
                      country={sessionUser?.TenantCode?.toLowerCase() || "pl"}
                      containerClass={classes.phoneInput}
                      inputProps={{ readOnly: true }}
                      disabled
                    />
                  </Grid>

                  <Grid item xs={12} md={1}>
                    <IconButton
                      onClick={() => deletePhoneItem(index)}
                      style={{
                        height: 44,
                        width: "100%",
                        borderRadius: 12,
                        color: "#e53e3e",
                        background: "transparent",
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Dialog
          open={dialogOpen}
          onClose={handleDialogNo}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            style: {
              borderRadius: 16,
              overflow: "hidden",
              background: "#fdfefe",
              boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
            },
          }}
        >
          <DialogTitle
            style={{
              background: "linear-gradient(90deg, #00B2FF, #0079FF)",
              color: "#fff",
              fontWeight: 700,
              fontSize: 17,
              textAlign: "center",
              padding: "14px 0",
              letterSpacing: 0.3,
            }}
          >
            ⚠️{t("existingConsumerFound")}
          </DialogTitle>

          <DialogContent
            dividers
            style={{
              padding: "24px 28px",
              overflowX: "hidden",
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              textAlign="center"
              gap={1}
            >
              <Typography
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: "#2d3748",
                }}
              >
                {t("numberAlreadyRegistered", "Bu numara zaten sistemde kayıtlı:")}
              </Typography>

              <Box
                mt={1.5}
                p={2}
                width="100%"
                style={{
                  background: "linear-gradient(90deg,#eaf4ff,#f6fbff)",
                  borderRadius: 12,
                  border: "1px solid #cfe6ff",
                }}
              >
                <Typography
                  style={{
                    fontWeight: 700,
                    color: "#1b84ff",
                    fontSize: 15,
                  }}
                >
                  {existingConsumer?.FirstName} {existingConsumer?.LastName}
                </Typography>
                <Typography
                  style={{
                    fontSize: 13,
                    color: "#4a5568",
                    marginTop: 4,
                  }}
                >
                  {t("id", "ID")}: {existingConsumer?.ConsumerId}
                </Typography>
              </Box>

              <Typography
                style={{
                  marginTop: 18,
                  fontSize: 14,
                  color: "#4a5568",
                  lineHeight: 1.5,
                }}
              >
                {t("goToConsumerQuestion")}
              </Typography>
            </Box>
          </DialogContent>

          <DialogActions
            style={{
              padding: "12px 20px 20px",
              justifyContent: "center",
              gap: 12,
            }}
          >
            <Button
              onClick={handleDialogNo}
              style={{
                minWidth: 100,
                borderRadius: 999,
                fontWeight: 600,
                textTransform: "none",
                border: "1px solid #cbd5e0",
                color: "#2d3748",
                background: "#fff",
              }}
            >
              {t("no", "Hayır")}
            </Button>
            <Button
              onClick={handleDialogYes}
              style={{
                minWidth: 100,
                borderRadius: 999,
                fontWeight: 600,
                textTransform: "none",
                background: "linear-gradient(90deg, #00B2FF, #0079FF)",
                color: "#fff",
                boxShadow: "0 4px 10px rgba(27,132,255,0.3)",
              }}
            >
              {t("yes", "Evet")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar (bilgi mesajları için) */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.type}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </CardContent>
    </Card>
  );
}
